import { useState } from "react"
import styled from "styled-components"

import { Banner, Skeleton, spacing, Text } from "@minuthq/meatball-ui-react"

import { AiAssistantCreateContextDialog } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContextLibrary/AiAssistantCreateContextDialog"
import { AiAssistantAcceptSuggestionDialog } from "src/components/AiAssistant/AiAssistantSuggestions/AiAssistantAcceptSuggestionDialog"
import { AiAssistantSuggestionCard } from "src/components/AiAssistant/AiAssistantSuggestions/AiAssistantSuggestionCard"
import { NoAiSuggestions } from "src/components/EmptyState/NoAiSuggestions"
import { Pager } from "src/components/Pager/Pager"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import {
  useFetchPropertyContextSuggestions,
  usePatchPropertyContextSuggestion,
} from "src/data/propertyContext/propertyContextQueries"
import { TPropertyContextSuggestion } from "src/data/propertyContext/propertyContextTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { useSearchParams } from "src/router/useSearchParams"
import { DefaultBox } from "src/ui/Layout/DefaultBox"
import { ErrorService } from "src/utils/ErrorService"

export function AiAssistantSuggestions() {
  const { t, langKeys, tPlaceholder } = useTranslate()

  const { navigate } = useRouter()

  const { org } = useOrganization()

  const [createNewContextDialog, setCreateNewContextDialog] = useState<{
    open: boolean
    suggestion: TPropertyContextSuggestion | null
  } | null>(null)

  const [acceptSuggestionDialog, setAcceptSuggestionDialog] = useState<{
    open: boolean
    suggestion: TPropertyContextSuggestion | null
  }>({
    open: false,
    suggestion: null,
  })

  const { searchParams, setSearchParams } = useSearchParams({
    keys: [
      {
        key: "offset",
        type: "number",
      },
    ],
  })

  const limit = 15
  const fetchSuggestions = useFetchPropertyContextSuggestions({
    orgId: org.id,
    filters: {
      statuses: ["pending"],
      limit,
      offset: searchParams.offset ?? 0,
    },
  })

  const patchPropertyContextSuggestion = usePatchPropertyContextSuggestion()

  const suggestionLoadingId =
    patchPropertyContextSuggestion.isPending &&
    patchPropertyContextSuggestion.variables?.path
      .property_context_suggestion_id

  const suggestions = fetchSuggestions.data?.property_context_suggestions || []

  if (fetchSuggestions.isLoading) {
    return (
      <LoadingWrapper>
        <Skeleton variant="rect" width="30ch" height={16} />
        <Skeleton variant="rect" width="20ch" height={16} />
        <Skeleton variant="rect" width="45ch" height={16} />
      </LoadingWrapper>
    )
  }

  if (fetchSuggestions.isError) {
    return (
      <Banner type="error" fullWidth>
        {t(langKeys.failed_contact_support)}
      </Banner>
    )
  }

  if (suggestions.length === 0) {
    return (
      <DefaultBox size="small">
        <NoAiSuggestions />
      </DefaultBox>
    )
  }

  return (
    <DefaultBox size="small">
      <Text variant="body" marginBottom={spacing.L}>
        {tPlaceholder(
          "Autopilot reviews your manual replies to guests and suggests updates to your knowledge base. This helps improve response accuracy and reduce future escalations."
        )}
      </Text>

      <SuggestionsListGrid>
        {suggestions.map((suggestion) => (
          <AiAssistantSuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            loading={suggestion.id === suggestionLoadingId}
            onAcceptSuggestion={() => {
              setAcceptSuggestionDialog({
                open: true,
                suggestion,
              })
            }}
            onRejectSuggestion={() => {
              patchPropertyContextSuggestion.mutate({
                path: {
                  organization_id: org.id,
                  property_context_suggestion_id: suggestion.id,
                },
                body: { status: "rejected" },
              })
            }}
          />
        ))}
      </SuggestionsListGrid>

      <Pager
        limit={limit}
        offset={searchParams.offset ?? 0}
        setOffset={(offset) => {
          setSearchParams("offset", offset)
        }}
        totalCount={fetchSuggestions.data.paging.total_count}
      />

      {acceptSuggestionDialog.open && acceptSuggestionDialog.suggestion && (
        <AiAssistantAcceptSuggestionDialog
          suggestion={acceptSuggestionDialog.suggestion}
          onClose={() => {
            setAcceptSuggestionDialog({ open: false, suggestion: null })
          }}
          onConfirm={({ createNewContext, editExistingContext }) => {
            const suggestion = acceptSuggestionDialog.suggestion

            if (!suggestion) {
              ErrorService.captureException(
                "No suggestion found when accepting suggestion"
              )
              return
            }

            if (createNewContext) {
              setCreateNewContextDialog({
                open: true,
                suggestion,
              })
            }

            if (editExistingContext) {
              navigate(
                Routes.AiAssistantKnowledgeContent.location({
                  contextId: editExistingContext.id,
                  suggestionId: suggestion.id,
                })
              )
            }

            setAcceptSuggestionDialog({ open: false, suggestion: null })
          }}
        />
      )}

      {createNewContextDialog?.open && createNewContextDialog.suggestion && (
        <AiAssistantCreateContextDialog
          suggestion={createNewContextDialog.suggestion}
          onClose={() => {
            setCreateNewContextDialog(null)
            setAcceptSuggestionDialog({
              open: false,
              suggestion: null,
            })
          }}
        />
      )}
    </DefaultBox>
  )
}

const LoadingWrapper = styled.div`
  display: grid;
  gap: ${spacing.S};
`

const SuggestionsListGrid = styled.div`
  display: grid;
  gap: ${spacing.XS};
  margin-bottom: ${spacing.L};
`
